import { MultipartBody } from '@microsoft/kiota-abstractions';
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { CrewBriefingTemplate, FileStreamResult } from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { getApiErrorMessages } from '@/util/error-utils.ts';
import {
  createFileDownloadResponseHandlerOption,
  downloadFile,
} from '@/util/file-download.ts';

export const DOCX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const MAXIMUM_CREW_BRIEFING_TEMPLATE_SIZE = 2 * 1024 * 1024;

function getErrorMessage(error: unknown, fallbackMessage: string): string {
  return getApiErrorMessages(error, fallbackMessage).join(' ');
}

async function createMultipartBody(file: File): Promise<MultipartBody> {
  const body = new MultipartBody();
  body.addOrReplacePart(
    'formFile',
    DOCX_CONTENT_TYPE,
    await file.arrayBuffer(),
    undefined,
    file.name,
  );
  return body;
}

export const useCharterCrewBriefingTemplatesStore = defineStore(
  'charter-crew-briefing-templates',
  () => {
    const api = useApiClient();

    const templates = ref<CrewBriefingTemplate[]>([]);
    const loading = ref(false);
    const uploading = ref(false);
    const downloadingFileName = ref<string | null>(null);
    const deletingFileName = ref<string | null>(null);
    const error = ref<string | null>(null);

    const hasTemplates = computed(() => templates.value.length > 0);

    function clearError(): void {
      error.value = null;
    }

    async function fetchTemplates(): Promise<void> {
      loading.value = true;
      clearError();

      try {
        templates.value = await api.charter.crewBriefingTemplates.get() ?? [];
      } catch (caughtError) {
        error.value = getErrorMessage(caughtError, 'Crew briefing templates could not be loaded.');
        throw caughtError;
      } finally {
        loading.value = false;
      }
    }

    async function uploadTemplate(file: File): Promise<CrewBriefingTemplate | undefined> {
      uploading.value = true;
      clearError();

      try {
        const uploadedTemplate = await api.charter.crewBriefingTemplates.post(
          await createMultipartBody(file),
        );
        await fetchTemplates();
        return uploadedTemplate;
      } catch (caughtError) {
        error.value = getErrorMessage(caughtError, 'Crew briefing template could not be uploaded.');
        throw caughtError;
      } finally {
        uploading.value = false;
      }
    }

    async function downloadTemplate(fileName: string): Promise<FileStreamResult | undefined> {
      downloadingFileName.value = fileName;
      clearError();

      try {
        const file = await api.charter.crewBriefingTemplates.byFileName(fileName).get({
          options: [createFileDownloadResponseHandlerOption()],
        });
        return downloadFile(file, fileName);
      } catch (caughtError) {
        error.value = getErrorMessage(caughtError, 'Crew briefing template could not be downloaded.');
        throw caughtError;
      } finally {
        downloadingFileName.value = null;
      }
    }

    async function deleteTemplate(fileName: string): Promise<void> {
      deletingFileName.value = fileName;
      clearError();

      try {
        await api.charter.crewBriefingTemplates.byFileName(fileName).delete();
        await fetchTemplates();
      } catch (caughtError) {
        error.value = getErrorMessage(caughtError, 'Crew briefing template could not be deleted.');
        throw caughtError;
      } finally {
        deletingFileName.value = null;
      }
    }

    return {
      templates,
      loading,
      uploading,
      downloadingFileName,
      deletingFileName,
      error,
      hasTemplates,
      clearError,
      fetchTemplates,
      uploadTemplate,
      downloadTemplate,
      deleteTemplate,
    };
  },
);
